import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Alert,
    CircularProgress,
    TextField,
    Grid,
    FormControlLabel,
    Switch,
    IconButton,
    Tooltip,
    Snackbar,
    Divider,
    Card,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    InputAdornment
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    MedicalServices as MedicalIcon,
    Close as CloseIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    ExpandMore as ExpandMoreIcon,
    AccessTime as TimeIcon,
    Healing as HealingIcon,
    LocalHospital as HospitalIcon,
    Assignment as AssignmentIcon,
    ViewList as BatchIcon
} from '@mui/icons-material';
import apiClient from '../services/apiClient';

interface DiaryOrder {
    id: string;
    diary_id: string;
    healthcare_service_id?: string;
    cur_code: string;
    duration_mins: number;
    notes?: string;
    availability_delay: number;
    blood_draw: boolean;
    all_diagnosis: boolean;
    created_at?: string;
    updated_at?: string;
}

interface OrderForm {
    healthcare_service_id: string;
    cur_code: string;
    duration_mins: number;
    notes: string;
    availability_delay: number;
    blood_draw: boolean;
    all_diagnosis: boolean;
}

interface OrderStats {
    total_orders: number;
    blood_draw_orders: number;
    all_diagnosis_orders: number;
    avg_duration: number;
    unique_services: number;
}

interface DiaryOrdersDialogProps {
    open: boolean;
    onClose: () => void;
    diaryId: string;
    diaryName: string;
}

// Prestazioni FKT predefinite per testing
const PREDEFINED_FKT_SERVICES = [
    { cur_code: '93.69.1', name: 'Fisioterapia individuale' },
    { cur_code: '93.78.2', name: 'Terapia occupazionale' },
    { cur_code: '93.87.1', name: 'Rieducazione motoria individuale' },
    { cur_code: '93.13.2', name: 'Massoterapia terapeutica' },
    { cur_code: '93.22.1', name: 'Elettroterapia antalgica' },
    { cur_code: '93.35.1', name: 'Ultrasuonoterapia' },
    { cur_code: '93.11.1', name: 'Rieducazione posturale' }
];

const DiaryOrdersDialog: React.FC<DiaryOrdersDialogProps> = ({
    open,
    onClose,
    diaryId,
    diaryName
}) => {
    const [orders, setOrders] = useState<DiaryOrder[]>([]);
    const [stats, setStats] = useState<OrderStats | null>(null);
    const [loading, setLoading] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [showBatchForm, setShowBatchForm] = useState(false);
    const [editingOrder, setEditingOrder] = useState<DiaryOrder | null>(null);
    
    // Form state
    const [form, setForm] = useState<OrderForm>({
        healthcare_service_id: '',
        cur_code: '',
        duration_mins: 15,
        notes: '',
        availability_delay: 0,
        blood_draw: false,
        all_diagnosis: false
    });
    
    // Batch form
    const [batchCodes, setBatchCodes] = useState<string>('');
    
    // Snackbar
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success' as 'success' | 'error'
    });

    // Load orders when dialog opens
    useEffect(() => {
        if (open && diaryId) {
            loadOrders();
            loadStats();
        }
    }, [open, diaryId]);

    const loadOrders = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.get(`/diary-orders/${diaryId}`);
            if (response.data.success) {
                const ordersData = response.data.data;
                if (Array.isArray(ordersData)) {
                    setOrders(ordersData);
                } else {
                    console.warn('Backend returned non-array data for orders:', ordersData);
                    setOrders([]);
                }
            } else {
                setError(response.data.error || 'Errore nel caricamento degli ordini agenda');
                setOrders([]);
            }
        } catch (err: any) {
            const errorMessage = err.response?.data?.error || err.message || 'Errore nel caricamento degli ordini agenda';
            console.error('Error loading diary orders:', errorMessage);
            setError(`Impossibile caricare gli ordini agenda: ${errorMessage}`);
            setOrders([]);
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const response = await apiClient.get(`/diary-orders/stats/${diaryId}`);
            if (response.data.success) {
                setStats(response.data.data);
            }
        } catch (err: any) {
            console.error('Error loading diary orders stats:', err.message);
        }
    };

    const handleCreateOrder = async () => {
        // Validazione
        if (!form.cur_code.trim()) {
            setSnackbar({ 
                open: true, 
                message: 'Il codice CUR è obbligatorio', 
                severity: 'error' 
            });
            return;
        }

        if (form.duration_mins < 1 || form.duration_mins > 120) {
            setSnackbar({ 
                open: true, 
                message: 'La durata deve essere tra 1 e 120 minuti', 
                severity: 'error' 
            });
            return;
        }

        setFormLoading(true);
        try {
            const payload = {
                healthcare_service_id: form.healthcare_service_id || null,
                cur_code: form.cur_code.trim(),
                duration_mins: form.duration_mins,
                notes: form.notes.trim() || null,
                availability_delay: form.availability_delay,
                blood_draw: form.blood_draw,
                all_diagnosis: form.all_diagnosis
            };

            const response = await apiClient.post(`/diary-orders/${diaryId}`, payload);
            
            if (response.data.success) {
                setSnackbar({ 
                    open: true, 
                    message: 'Ordine agenda creato con successo!', 
                    severity: 'success' 
                });
                
                resetForm();
                setShowForm(false);
                await loadOrders();
                await loadStats();
            } else {
                setSnackbar({ 
                    open: true, 
                    message: response.data.error || 'Errore nella creazione dell\'ordine', 
                    severity: 'error' 
                });
            }
        } catch (err: any) {
            setSnackbar({ 
                open: true, 
                message: err.response?.data?.details || err.message || 'Errore nella creazione dell\'ordine', 
                severity: 'error' 
            });
        } finally {
            setFormLoading(false);
        }
    };

    const handleUpdateOrder = async () => {
        if (!editingOrder) return;

        setFormLoading(true);
        try {
            const payload = {
                healthcare_service_id: form.healthcare_service_id || null,
                cur_code: form.cur_code.trim(),
                duration_mins: form.duration_mins,
                notes: form.notes.trim() || null,
                availability_delay: form.availability_delay,
                blood_draw: form.blood_draw,
                all_diagnosis: form.all_diagnosis
            };

            const response = await apiClient.put(
                `/diary-orders/${diaryId}/${editingOrder.id}`, 
                payload
            );
            
            if (response.data.success) {
                setSnackbar({ 
                    open: true, 
                    message: 'Ordine agenda aggiornato con successo!', 
                    severity: 'success' 
                });
                
                resetForm();
                setShowForm(false);
                setEditingOrder(null);
                await loadOrders();
                await loadStats();
            } else {
                setSnackbar({ 
                    open: true, 
                    message: response.data.error || 'Errore nell\'aggiornamento dell\'ordine', 
                    severity: 'error' 
                });
            }
        } catch (err: any) {
            setSnackbar({ 
                open: true, 
                message: err.response?.data?.details || err.message || 'Errore nell\'aggiornamento dell\'ordine', 
                severity: 'error' 
            });
        } finally {
            setFormLoading(false);
        }
    };

    const handleDeleteOrder = async (order: DiaryOrder) => {
        const confirmed = window.confirm(
            `Sei sicuro di voler eliminare l'ordine "${order.cur_code}"?\n\n` +
            `Servizio: ${order.healthcare_service_id || 'N/A'}\n` +
            `Durata: ${order.duration_mins} min\n\n` +
            `L'eliminazione è permanente.`
        );
        
        if (!confirmed) return;

        try {
            const response = await apiClient.delete(
                `/diary-orders/${diaryId}/${order.id}`,
                {
                    data: { reason: 'Eliminazione ordine richiesta dall\'utente' }
                }
            );
            
            if (response.data.success) {
                setSnackbar({ 
                    open: true, 
                    message: 'Ordine eliminato con successo!', 
                    severity: 'success' 
                });
                await loadOrders();
                await loadStats();
            } else {
                setSnackbar({ 
                    open: true, 
                    message: response.data.error || 'Errore nell\'eliminazione dell\'ordine', 
                    severity: 'error' 
                });
            }
        } catch (err: any) {
            setSnackbar({ 
                open: true, 
                message: err.response?.data?.details || err.message || 'Errore nell\'eliminazione dell\'ordine', 
                severity: 'error' 
            });
        }
    };

    const handleEditOrder = (order: DiaryOrder) => {
        setEditingOrder(order);
        setForm({
            healthcare_service_id: order.healthcare_service_id || '',
            cur_code: order.cur_code,
            duration_mins: order.duration_mins,
            notes: order.notes || '',
            availability_delay: order.availability_delay,
            blood_draw: order.blood_draw,
            all_diagnosis: order.all_diagnosis
        });
        setShowForm(true);
    };

    const handleBatchCreate = async () => {
        if (!batchCodes.trim()) {
            setSnackbar({ 
                open: true, 
                message: 'Inserisci almeno un codice CUR', 
                severity: 'error' 
            });
            return;
        }

        const codes = batchCodes.split('\n').filter(code => code.trim());
        
        if (codes.length === 0) {
            setSnackbar({ 
                open: true, 
                message: 'Nessun codice valido trovato', 
                severity: 'error' 
            });
            return;
        }

        setFormLoading(true);
        try {
            const orders = codes.map(code => ({
                cur_code: code.trim(),
                duration_mins: 15,
                availability_delay: 0,
                blood_draw: false,
                all_diagnosis: false
            }));

            const response = await apiClient.post(`/diary-orders/${diaryId}/batch`, { orders });
            
            if (response.data.success) {
                const { successful, failed } = response.data.data.summary;
                setSnackbar({ 
                    open: true, 
                    message: `Batch completato: ${successful} creati, ${failed} errori`, 
                    severity: successful > 0 ? 'success' : 'error' 
                });
                
                setBatchCodes('');
                setShowBatchForm(false);
                await loadOrders();
                await loadStats();
            } else {
                setSnackbar({ 
                    open: true, 
                    message: response.data.error || 'Errore nella creazione batch', 
                    severity: 'error' 
                });
            }
        } catch (err: any) {
            setSnackbar({ 
                open: true, 
                message: err.response?.data?.details || err.message || 'Errore nella creazione batch', 
                severity: 'error' 
            });
        } finally {
            setFormLoading(false);
        }
    };

    const resetForm = () => {
        setForm({
            healthcare_service_id: '',
            cur_code: '',
            duration_mins: 15,
            notes: '',
            availability_delay: 0,
            blood_draw: false,
            all_diagnosis: false
        });
        setEditingOrder(null);
    };

    const loadPredefinedService = (service: any) => {
        setForm(prev => ({
            ...prev,
            cur_code: service.cur_code,
            healthcare_service_id: service.name
        }));
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('it-IT');
    };

    return (
        <>
            <Dialog 
                open={open} 
                onClose={onClose}
                maxWidth="xl"
                fullWidth
                PaperProps={{
                    sx: { minHeight: '85vh' }
                }}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <MedicalIcon />
                            <Typography variant="h6">
                                Ordini/Prestazioni Agenda: {diaryName}
                            </Typography>
                        </Box>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {/* Statistiche */}
                    {stats && (
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} md={2.4}>
                                <Card variant="outlined">
                                    <CardContent sx={{ textAlign: 'center' }}>
                                        <Typography color="textSecondary" gutterBottom>
                                            Totale Ordini
                                        </Typography>
                                        <Typography variant="h4">
                                            {stats.total_orders}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={2.4}>
                                <Card variant="outlined">
                                    <CardContent sx={{ textAlign: 'center' }}>
                                        <Typography color="textSecondary" gutterBottom>
                                            Con Prelievo
                                        </Typography>
                                        <Typography variant="h4" color="error.main">
                                            {stats.blood_draw_orders}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={2.4}>
                                <Card variant="outlined">
                                    <CardContent sx={{ textAlign: 'center' }}>
                                        <Typography color="textSecondary" gutterBottom>
                                            Tutte Diagnosi
                                        </Typography>
                                        <Typography variant="h4" color="warning.main">
                                            {stats.all_diagnosis_orders}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={2.4}>
                                <Card variant="outlined">
                                    <CardContent sx={{ textAlign: 'center' }}>
                                        <Typography color="textSecondary" gutterBottom>
                                            Durata Media
                                        </Typography>
                                        <Typography variant="h4" color="info.main">
                                            {Math.round(stats.avg_duration)}min
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={2.4}>
                                <Card variant="outlined">
                                    <CardContent sx={{ textAlign: 'center' }}>
                                        <Typography color="textSecondary" gutterBottom>
                                            Servizi Unici
                                        </Typography>
                                        <Typography variant="h4" color="success.main">
                                            {stats.unique_services}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    )}

                    {/* Controlli */}
                    <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => {
                                resetForm();
                                setShowForm(!showForm);
                                setShowBatchForm(false);
                            }}
                            color={showForm ? "secondary" : "primary"}
                        >
                            {showForm ? 'Nascondi Form' : 'Nuovo Ordine'}
                        </Button>
                        
                        <Button
                            variant="outlined"
                            startIcon={<BatchIcon />}
                            onClick={() => {
                                setShowBatchForm(!showBatchForm);
                                setShowForm(false);
                                setBatchCodes('');
                            }}
                            color={showBatchForm ? "secondary" : "primary"}
                        >
                            {showBatchForm ? 'Nascondi Batch' : 'Batch Import'}
                        </Button>
                        
                        <Button
                            variant="outlined" 
                            onClick={() => { loadOrders(); loadStats(); }}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={16} /> : undefined}
                        >
                            Ricarica
                        </Button>
                    </Box>

                    {/* Form Creazione/Modifica Ordine */}
                    {showForm && (
                        <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    {editingOrder ? `Modifica Ordine: ${editingOrder.cur_code}` : 'Crea Nuovo Ordine/Prestazione'}
                                </Typography>
                                
                                {/* Servizi Predefiniti FKT */}
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Servizi FKT Predefiniti:
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {PREDEFINED_FKT_SERVICES.map((service) => (
                                            <Chip
                                                key={service.cur_code}
                                                label={`${service.cur_code} - ${service.name}`}
                                                onClick={() => loadPredefinedService(service)}
                                                color="primary"
                                                variant="outlined"
                                                size="small"
                                                clickable
                                            />
                                        ))}
                                    </Box>
                                </Box>

                                <Divider sx={{ mb: 2 }} />
                                
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="Codice CUR *"
                                            value={form.cur_code}
                                            onChange={(e) => setForm(prev => ({ ...prev, cur_code: e.target.value }))}
                                            placeholder="Es: 93.69.1"
                                            required
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <AssignmentIcon fontSize="small" />
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="ID Servizio Sanitario"
                                            value={form.healthcare_service_id}
                                            onChange={(e) => setForm(prev => ({ ...prev, healthcare_service_id: e.target.value }))}
                                            placeholder="Es: Fisioterapia"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Durata (minuti) *"
                                            value={form.duration_mins}
                                            onChange={(e) => setForm(prev => ({ ...prev, duration_mins: parseInt(e.target.value) || 15 }))}
                                            inputProps={{ min: 1, max: 120 }}
                                            required
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <TimeIcon fontSize="small" />
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>
                                    
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={2}
                                            label="Note (opzionale)"
                                            value={form.notes}
                                            onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                                            placeholder="Note aggiuntive per l'ordine..."
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Ritardo Disponibilità (minuti)"
                                            value={form.availability_delay}
                                            onChange={(e) => setForm(prev => ({ ...prev, availability_delay: parseInt(e.target.value) || 0 }))}
                                            inputProps={{ min: 0, max: 1440 }}
                                        />
                                    </Grid>
                                    
                                    <Grid item xs={12} md={6}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={form.blood_draw}
                                                    onChange={(e) => setForm(prev => ({ ...prev, blood_draw: e.target.checked }))}
                                                />
                                            }
                                            label="Richiede Prelievo"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={form.all_diagnosis}
                                                    onChange={(e) => setForm(prev => ({ ...prev, all_diagnosis: e.target.checked }))}
                                                />
                                            }
                                            label="Tutte le Diagnosi"
                                        />
                                    </Grid>
                                </Grid>

                                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                    <Button
                                        variant="contained"
                                        onClick={editingOrder ? handleUpdateOrder : handleCreateOrder}
                                        disabled={formLoading || !form.cur_code.trim()}
                                        startIcon={formLoading ? <CircularProgress size={16} /> : <SaveIcon />}
                                    >
                                        {formLoading 
                                            ? (editingOrder ? 'Aggiornamento...' : 'Creazione...') 
                                            : (editingOrder ? 'Aggiorna Ordine' : 'Crea Ordine')
                                        }
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            setShowForm(false);
                                            resetForm();
                                        }}
                                        startIcon={<CancelIcon />}
                                    >
                                        Annulla
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    {/* Form Batch Import */}
                    {showBatchForm && (
                        <Card sx={{ mb: 3, bgcolor: 'warning.50' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Importazione Batch Codici CUR
                                </Typography>
                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                    Inserisci un codice CUR per riga. Verranno creati con durata 15min e impostazioni di default.
                                </Typography>
                                
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={6}
                                    label="Codici CUR (uno per riga)"
                                    value={batchCodes}
                                    onChange={(e) => setBatchCodes(e.target.value)}
                                    placeholder="93.69.1&#10;93.78.2&#10;93.87.1"
                                    sx={{ mb: 2 }}
                                />

                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleBatchCreate}
                                        disabled={formLoading || !batchCodes.trim()}
                                        startIcon={formLoading ? <CircularProgress size={16} /> : <BatchIcon />}
                                        color="warning"
                                    >
                                        {formLoading ? 'Importazione...' : 'Importa Batch'}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            setShowBatchForm(false);
                                            setBatchCodes('');
                                        }}
                                        startIcon={<CancelIcon />}
                                    >
                                        Annulla
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    <Divider sx={{ my: 2 }} />

                    {/* Lista Ordini */}
                    <Typography variant="h6" gutterBottom>
                        Ordini Esistenti ({orders.length})
                    </Typography>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : orders.length === 0 ? (
                        <Alert severity="info">
                            Nessun ordine trovato per questa agenda. Utilizza il form sopra per crearne uno.
                        </Alert>
                    ) : (
                        <Box>
                            {orders.map((order) => (
                                <Accordion key={order.id} sx={{ mb: 1 }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                            <AssignmentIcon color="primary" />
                                            <Typography variant="h6" sx={{ flexGrow: 1 }}>
                                                {order.cur_code}
                                            </Typography>
                                            <Chip 
                                                label={`${order.duration_mins}min`} 
                                                color="info" 
                                                size="small" 
                                            />
                                            {order.blood_draw && (
                                                <Chip 
                                                    label="Prelievo" 
                                                    color="error" 
                                                    size="small" 
                                                />
                                            )}
                                            {order.all_diagnosis && (
                                                <Chip 
                                                    label="Tutte Diagnosi" 
                                                    color="warning" 
                                                    size="small" 
                                                />
                                            )}
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                                    <strong>Servizio:</strong> {order.healthcare_service_id || 'N/A'}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                                    <strong>Durata:</strong> {order.duration_mins} minuti
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                                    <strong>Ritardo:</strong> {order.availability_delay} min
                                                </Typography>
                                                {order.notes && (
                                                    <Typography variant="body2" color="textSecondary" gutterBottom>
                                                        <strong>Note:</strong> {order.notes}
                                                    </Typography>
                                                )}
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                                    <strong>Creato:</strong> {order.created_at ? formatDate(order.created_at) : 'N/A'}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                                    <strong>Modificato:</strong> {order.updated_at ? formatDate(order.updated_at) : 'N/A'}
                                                </Typography>
                                                
                                                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                                    {order.blood_draw && (
                                                        <Chip 
                                                            label="Richiede Prelievo" 
                                                            color="error" 
                                                            size="small" 
                                                            variant="outlined"
                                                        />
                                                    )}
                                                    {order.all_diagnosis && (
                                                        <Chip 
                                                            label="Tutte le Diagnosi" 
                                                            color="warning" 
                                                            size="small" 
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Box>
                                            </Grid>
                                        </Grid>
                                        
                                        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                            <Tooltip title="Modifica ordine">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<EditIcon />}
                                                    onClick={() => handleEditOrder(order)}
                                                >
                                                    Modifica
                                                </Button>
                                            </Tooltip>
                                            <Tooltip title="Elimina ordine">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="error"
                                                    startIcon={<DeleteIcon />}
                                                    onClick={() => handleDeleteOrder(order)}
                                                >
                                                    Elimina
                                                </Button>
                                            </Tooltip>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={onClose}>
                        Chiudi
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar per notifiche */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            >
                <Alert 
                    severity={snackbar.severity} 
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default DiaryOrdersDialog;